// Self-contained Top Performers widget: owns the US / EU market toggle, the
// 1D / 7D / 30D horizon toggle, the TanStack Query fetch, and the
// loading / error / empty states. Drop it into any page with a bare
// <TopPerformersWidget />.
//
// 1D shows a paired Gainers / Losers layout (biggest up + down movers of the
// session); 7D and 30D show a single trend-leaders board. All ranking + slicing
// is done server-side and the payload arrives PRE-SPLIT into US and EU boards,
// so switching markets is an instant, zero-network client-side toggle over the
// cached TanStack Query payload.

import { useState } from 'react';

import { Flame } from 'lucide-react';

import {
    useTopPerformers,
    type GainKey,
    type Market,
    type PerformerRow,
    type TopPerformersResponse,
} from '../api/useTopPerformers';
import { PerformersTable } from './PerformersTable';

type Horizon = '1D' | '7D' | '30D';

const HORIZONS: Horizon[] = ['1D', '7D', '30D'];
const MARKETS: Market[] = ['US', 'EU'];

const GAIN_KEY: Record<Horizon, GainKey> = {
    '1D': 'gain_1d',
    '7D': 'gain_7d',
    '30D': 'gain_30d',
};

const MARKET_LABEL: Record<Market, string> = {
    US: 'US (NYSE / NASDAQ)',
    EU: 'EU (XETRA / Vienna)',
};

// Pick the correct pre-split board arrays for the active market. The backend
// already ranked + sliced each market independently, so this is a pure lookup.
interface MarketBoards {
    top_1d: PerformerRow[];
    worst_1d: PerformerRow[];
    top_7d: PerformerRow[];
    top_30d: PerformerRow[];
}

const boardsForMarket = (
    data: TopPerformersResponse,
    market: Market,
): MarketBoards =>
    market === 'US'
        ? {
              top_1d: data.top_1d_us,
              worst_1d: data.worst_1d_us,
              top_7d: data.top_7d_us,
              top_30d: data.top_30d_us,
          }
        : {
              top_1d: data.top_1d_eu,
              worst_1d: data.worst_1d_eu,
              top_7d: data.top_7d_eu,
              top_30d: data.top_30d_eu,
          };

export const TopPerformersWidget = () => {
    const [horizon, setHorizon] = useState<Horizon>('1D');
    const [market, setMarket] = useState<Market>('US');
    const { data, isLoading, isError } = useTopPerformers();

    const boards = data ? boardsForMarket(data, market) : null;

    return (
        <div className="flex flex-col space-y-6">
            {/* Header + horizon toggle */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h2 className="text-xl font-bold flex items-center gap-2 text-slate-900 dark:text-white">
                        <Flame className="w-5 h-5 text-amber-500" />
                        Top Performers: Biggest Movers
                    </h2>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-3xl">
                        Cumulative 1-day, 7-day and 30-day leaders across
                        institutional-grade US equities (NYSE / NASDAQ) and EU
                        names (XETRA / Vienna), carefully screened based on proprietary Forge Ultra signal &amp; liquidity.
                    </p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    {/* Market toggle (US / EU) */}
                    <div className="flex rounded-lg bg-slate-100 p-1 dark:bg-slate-800">
                        {MARKETS.map((m) => (
                            <button
                                key={m}
                                type="button"
                                onClick={() => setMarket(m)}
                                title={MARKET_LABEL[m]}
                                className={`px-4 py-1.5 text-sm font-bold rounded-md transition-all ${
                                    market === m
                                        ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-700 dark:text-white'
                                        : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                                }`}
                            >
                                {m}
                            </button>
                        ))}
                    </div>

                    {/* Horizon toggle (1D / 7D / 30D) */}
                    <div className="flex rounded-lg bg-slate-100 p-1 dark:bg-slate-800">
                        {HORIZONS.map((h) => (
                            <button
                                key={h}
                                type="button"
                                onClick={() => setHorizon(h)}
                                className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${
                                    horizon === h
                                        ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-700 dark:text-white'
                                        : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                                }`}
                            >
                                {h}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="min-h-[400px]">
                {isLoading ? (
                    <div className="flex h-[400px] items-center justify-center rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-800">
                        <div className="animate-pulse font-medium text-slate-400">
                            Loading momentum leaderboard…
                        </div>
                    </div>
                ) : isError || !boards ? (
                    <div className="rounded-xl border border-red-100 bg-red-50 p-4 text-red-600 dark:border-red-900 dark:bg-red-900/20 dark:text-red-400">
                        Failed to load the top-performers leaderboard. Please try
                        again later.
                    </div>
                ) : horizon === '1D' ? (
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 lg:gap-8 items-stretch">
                        <PerformersTable
                            title={`Top Gainers (1D · ${market})`}
                            items={boards.top_1d}
                            gainKey="gain_1d"
                            theme="buy"
                        />
                        <PerformersTable
                            title={`Top Losers (1D · ${market})`}
                            items={boards.worst_1d}
                            gainKey="gain_1d"
                            theme="sell"
                        />
                    </div>
                ) : (
                    <PerformersTable
                        title={`Trend Leaders (${horizon} · ${market})`}
                        items={horizon === '7D' ? boards.top_7d : boards.top_30d}
                        gainKey={GAIN_KEY[horizon]}
                        theme="buy"
                    />
                )}
            </div>

            {/* Observability footer */}
            {data && (
                <div className="flex items-center justify-end gap-3 text-[10px] font-mono font-bold uppercase tracking-widest text-slate-400">
                    <span>Universe: {data.universe_size}</span>
                    <span className="text-emerald-500">
                        DB Scan: {data.execution_time_ms.toFixed(2)}ms
                    </span>
                </div>
            )}
        </div>
    );
};

export default TopPerformersWidget;
